/**
 * LCP (Largest Contentful Paint) Optimization Utilities
 * 
 * This file provides specific optimizations for improving LCP metrics
 * which is one of the Core Web Vitals and critical for user experience.
 */

// Track the LCP element and performance
let lcpElement = null;
let lcpObserver = null;
// eslint-disable-next-line no-unused-vars
let lcpStartTime = 0;

/**
 * Identifies and marks key elements that could be LCP candidates
 * for priority loading and rendering
 */
export const markLCPCandidates = () => {
  // Common LCP candidates include hero images, headlines, or large text blocks
  const candidates = [
    // Main content container
    document.querySelector('.main-content'),
    // Large hero images
    ...Array.from(document.querySelectorAll('img[width="100%"], img[height]')),
    // Headlines and large text
    ...Array.from(document.querySelectorAll('h1, h2')),
    // Application logo
    document.querySelector('img[alt*="logo" i], img[src*="logo" i]'),
  ].filter(Boolean);

  // Mark candidates with a data attribute
  candidates.forEach((el) => {
    el?.setAttribute('data-lcp-candidate', 'true');
    
    // If it's an image, we want to prioritize it
    if (el?.tagName === 'IMG') {
      el.setAttribute('loading', 'eager'); // Force eager loading
      el.setAttribute('fetchpriority', 'high'); // Set fetch priority to high
      
      // For images that are background images via CSS
      if (el.style.backgroundImage) {
        // Extract the URL
        const urlMatch = el.style.backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/i);
        if (urlMatch && urlMatch[1]) {
          // Preload the background image
          const link = document.createElement('link');
          link.rel = 'preload';
          link.href = urlMatch[1];
          link.as = 'image';
          document.head.appendChild(link);
        }
      }
    }
  });
  
  console.log(`👁️ Marked ${candidates.length} LCP candidates for optimization`);
  return candidates;
};

/**
 * Monitors and reports on LCP metrics
 */
export const monitorLCP = () => {
  if (!window.PerformanceObserver) return;
  
  lcpStartTime = performance.now();

  try {
    // Clean up any existing observer
    if (lcpObserver) {
      lcpObserver.disconnect();
    }

    // Create a new observer for LCP
    lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      if (entries.length > 0) {
        const lastEntry = entries[entries.length - 1];
        const lcpTime = lastEntry.startTime;
        
        // Get the element
        lcpElement = lastEntry.element;
        
        console.log(`📊 LCP detected in ${Math.round(lcpTime)}ms:`, lcpElement);
        
        // Add a visual indicator in development
        if (process.env.NODE_ENV === 'development' && lcpElement) {
          const originalOutline = lcpElement.style.outline;
          lcpElement.style.outline = '2px solid red';
          
          // Reset after 2 seconds
          setTimeout(() => {
            lcpElement.style.outline = originalOutline;
          }, 2000);
        }
        
        // Report to analytics in production
        if (process.env.NODE_ENV === 'production') {
          // Simple reporting - you can replace with your analytics system
          const payload = {
            metric: 'LCP',
            value: lcpTime,
            url: window.location.href,
            userAgent: navigator.userAgent
          };
          console.log('📈 LCP performance data:', payload);
          // In a real implementation, you'd send this to your analytics service
        }
      }
    });

    // Start observing LCP
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    
    return () => {
      if (lcpObserver) {
        lcpObserver.disconnect();
        lcpObserver = null;
      }
    };
  } catch (error) {
    console.error('Failed to monitor LCP:', error);
    return () => {};
  }
};

/**
 * Prioritizes critical CSS and JS resources
 */
export const prioritizeCriticalResources = () => {
  // Identify render-blocking resources
  const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
  const scripts = document.querySelectorAll('script[src]');
  
  // Adjust priorities for stylesheets
  stylesheets.forEach(link => {
    // If it's a critical stylesheet needed for LCP
    if (link.href.includes('main') || link.href.includes('critical')) {
      link.setAttribute('fetchpriority', 'high');
    } else {
      // Consider loading non-critical CSS asynchronously
      link.setAttribute('media', 'print');
      link.setAttribute('onload', "this.media='all'");
    }
  });
  
  // Adjust priorities for scripts
  scripts.forEach(script => {
    if (!script.getAttribute('async') && !script.getAttribute('defer')) {
      // Add defer to non-critical scripts
      if (!script.src.includes('critical') && !script.src.includes('initial')) {
        script.setAttribute('defer', '');
      }
    }
  });
};

/**
 * Applies all LCP optimizations
 */
export const applyLCPOptimizations = () => {
  console.log('🚀 Applying LCP optimizations...');
  
  // Start monitoring LCP
  const cleanupMonitoring = monitorLCP();
  
  // When DOM is ready
  const onDOMContentLoaded = () => {
    // Mark LCP candidates
    markLCPCandidates();
    
    // Prioritize critical resources
    prioritizeCriticalResources();
    
    console.log('✅ LCP optimizations applied');
  };
  
  // Apply optimizations once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onDOMContentLoaded);
  } else {
    onDOMContentLoaded();
  }
  
  // Return cleanup function
  return () => {
    cleanupMonitoring();
    document.removeEventListener('DOMContentLoaded', onDOMContentLoaded);
  };
};

const lcpOptimizations = {
  markLCPCandidates,
  monitorLCP,
  prioritizeCriticalResources,
  applyLCPOptimizations
};

export default lcpOptimizations;